<template>
  <div class="knw_widget_header_container">
    <nav class="knw_widget_header">
      <Avatar
        v-if="showAvatar"
        width="42px"
        height="42px"
        aria-hidden="true"
        :image="avatarUrl"
        @dblclick="writeVersionMessage"
      />
      <div class="knw_widget_header_content">
        <div class="knw_widget_header_content_body" :aria-label="headerTitles">
          <div class="knw_widget_header_title">
            {{ title }}
            <RatingButton
              v-if="rating && rating !== 0"
              :rating="rating"
              @click="handleRatingButtonClick"
            />
          </div>
          <span class="knw_widget_header_subtitle">{{ subtitle }}</span>
        </div>
      </div>
      <template v-if="showCloseButton">
        <button
          class="knw_widget_header_action"
          :aria-label="t('header.closebutton.label')"
          @click="emit('close')"
        >
          <IconClose aria-hidden="true" />
        </button>
      </template>
    </nav>

    <TransitionSlideRatingbar>
      <div v-if="showRatingBar" class="ratingbar">
        <!-- pass new rating through -->
        <RatingBar
          v-model="rating"
          @on-rating-set="
            (newRating) => {
              emit('onNewRatingSet', newRating);
            }
          "
        />
      </div>
    </TransitionSlideRatingbar>
  </div>
</template>

<script setup lang="ts">
import IconClose from "@/svg/IconClose.vue";

import appconfig from "@/appconfig";
import Avatar from "@/components/common/Avatar.vue";
import { useI18n } from "@/composables/useI18n";
import RatingBar from "@/components/common/RatingBar.vue";
import TransitionSlideRatingbar from "@/transitions/transition-slide-ratingbar.vue";
import { computed, ref, watch } from "vue";
import RatingButton from "@/components/common/RatingButton.vue";

const { currentRating, showRating, title, subtitle } = defineProps<{
  showCloseButton: boolean;
  showAvatar: boolean;
  avatarUrl: string;
  title: string;
  subtitle: string;
  showRating: boolean;
  currentRating: number;
}>();

const rating = ref<number>(currentRating);

watch(
  () => currentRating,
  (newValue) => {
    rating.value = newValue;
  }
);

const { t } = useI18n();
const emit = defineEmits(["close", "onAvatarClick", "onNewRatingSet", "onRatingButtonClick"]);

const handleRatingButtonClick = () => {
  emit("onRatingButtonClick");
};

const writeVersionMessage = async () => {
  emit("onAvatarClick", `moin.ai Widget Version: ${appconfig.appVersion}`);
};

const showRatingBar = computed(() => {
  return showRating;
});
const headerTitles = computed(() => {
  let str = title || "";
  if (subtitle) {
    str = str ? `${str}. ${subtitle}` : subtitle;
  }
  return str;
});
</script>

<style scoped lang="scss">
.knw_widget_header_container {
  position: relative;
}

.knw_widget_header {
  min-height: 70px;
  background-color: var(--moin-widget-header-background);
  color: var(--moin-widget-header-textcolor);
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  padding: 10px;
  align-items: center;
  transition: background-color 0.2s ease-out;
  width: 100%;
  position: relative;
  z-index: 200;
}

.knw_widget_header_action {
  width: 50px;
  height: 60px;
  min-width: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: $kw-widget-global-borderradius-inner;
  @include default-focus;

  &:focus-visible {
    outline-color: var(--moin-widget-header-textcolor);
  }

  &:hover svg {
    opacity: 0.9;
  }

  svg {
    fill: var(--moin-widget-header-textcolor);
    display: block;
    margin: 0 auto;
  }
}

.knw_widget_header_content {
  justify-self: start;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.knw_widget_header_content_body {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  .knw_widget_header_title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 20px;
    line-height: 1.2;
    color: var(--moin-widget-header-textcolor);
  }

  .knw_widget_header_subtitle {
    font-size: 15px;
    line-height: 1;
    color: var(--moin-widget-header-textcolor);
  }
}

.ratingbar {
  position: absolute;
  top: 105%;
  left: 1%;
  width: 98%;
  border-top: 1px solid #e0e0e0;
  box-shadow: $kw-operator-boxshadow;
  background: white;
  border-radius: 0.5rem;
  z-index: 100;
}
</style>
